import React from 'react'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'
import { formatTemperature, temperatureUnit } from '../../../utils/formatTemperature'

export interface Sensor {
  id: number
  sensor_id: string
  sensor_name: string
  sensor_type: string
  robot_node_id: number | null
  recorded_value: number | null
  recorded_at: string | null
}

interface SensorCardProps {
  sensor: Sensor
  localizationContext?: LocalizationContextType
  canEdit: boolean
  canDelete: boolean
  onEdit: (sensor: Sensor) => void
  onDelete: (sensor: Sensor) => void
}

// Stored as the beacon's MAC with underscores; MACs read as colon separated.
const formatSensorId = (sensorId: string) => sensorId.replace(/_/g, ':')

const SensorCard: React.FC<SensorCardProps> = ({
  sensor,
  localizationContext,
  canEdit,
  canDelete,
  onEdit,
  onDelete
}) => {
  const hasReading = sensor.recorded_value !== null

  // Delete must never ride along on a card click — it is reachable only through
  // its own badge, so the click is stopped from bubbling up to the edit handler.
  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDelete(sensor)
  }

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation()
    onEdit(sensor)
  }

  return (
    <div
      className={`sensor-card ${hasReading ? '' : 'sensor-card-quiet'} ${canEdit ? '' : 'sensor-card-readonly'}`}
      onClick={canEdit ? () => onEdit(sensor) : undefined}
    >
      {canEdit && (
        <button
          type="button"
          className="sensor-card-action sensor-card-edit"
          aria-label={`Edit ${sensor.sensor_name}`}
          title={`Edit ${sensor.sensor_name}`}
          onClick={handleEdit}
        >
          <i className="bi bi-pencil" />
        </button>
      )}

      {canDelete && (
        <button
          type="button"
          className="sensor-card-action sensor-card-delete"
          aria-label={`Delete ${sensor.sensor_name}`}
          title={`Delete ${sensor.sensor_name}`}
          onClick={handleDelete}
        >
          <i className="bi bi-trash" />
        </button>
      )}

      <div className="sensor-card-header">
        <span className="sensor-node-badge">Node {sensor.robot_node_id ?? '-'}</span>
      </div>

      <div className="sensor-card-name">{sensor.sensor_name}</div>

      <div className="sensor-temperature">
        {sensor.recorded_value !== null
          ? formatTemperature(sensor.recorded_value, localizationContext)
          : `-${temperatureUnit(localizationContext)}`}
      </div>

      <div className="sensor-mac">{formatSensorId(sensor.sensor_id)}</div>

      <div className="sensor-card-footer">
        <span className="sensor-last-seen-label">Last seen:</span>
        <span className="sensor-last-seen-value" title={timeSourceTitle(sensor.recorded_at, TIME_SOURCE.app)}>
          {sensor.recorded_at ? formatTenantDateTime(sensor.recorded_at, localizationContext) : '-'}
        </span>
      </div>
    </div>
  )
}

export default SensorCard
